class VideoSection extends HTMLElement {
  constructor() {
    super();
    this.playButton = this.querySelector('.video__play-button');
    this.muteButton = this.querySelector('.video__mute-button');
    this.video = this.querySelector('video');
    this.videoMobile = this.querySelector('.video__mobile-only video');
    this.videoDesktop = this.querySelector('.video__desktop-only video');
    this.iframe = this.querySelector('iframe');
    this.videoContainer = this.querySelector('.video__media');
    this.poster = this.querySelector('.video__poster');
    this.isAutoplay = this.video ? this.video.hasAttribute('autoplay') : false;
    this.isMuted = this.video ? this.video.hasAttribute('muted') : true;
    this.hasStartedPlaying = false;
    this.hasControls = this.video ? this.video.hasAttribute('controls') : false;

    // Get the active video based on screen size
    this.getActiveVideo = () => {
      if (this.videoMobile && window.innerWidth <= 749) {
        return this.videoMobile;
      }
      return this.videoDesktop || this.video;
    };

    // Update video reference when window resizes
    window.addEventListener('resize', () => {
      this.video = this.getActiveVideo();
    });

    // Initialize active video
    this.video = this.getActiveVideo();

    // Hide mute button only if muted setting is true
    if (this.muteButton && document.querySelector('video-section').dataset.muted === 'true') {
      this.muteButton.style.display = 'none';
    }

    if (this.playButton) {
      this.playButton.addEventListener('click', this.handlePlayButton.bind(this));
    }

    if (this.muteButton) {
      this.muteButton.addEventListener('click', this.toggleMute.bind(this));
      // Set initial mute button state
      this.updateMuteButtonState();
    }

    // Only add click handler to video if native controls are NOT enabled
    if (this.video && !this.hasControls) {
      this.video.addEventListener('click', this.toggleVideo.bind(this));
    }

    // Always add play/pause event listeners to sync UI state
    if (this.video) {
      this.video.addEventListener('play', () => {
        if (this.playButton) this.playButton.style.display = 'none';
        this.hasStartedPlaying = true;
      });

      this.video.addEventListener('pause', () => {
        if (this.playButton) this.playButton.style.display = 'flex';
      });
    }

    // Only add click handler to container if native controls are NOT enabled
    if (this.videoContainer && !this.hasControls) {
      this.videoContainer.addEventListener('click', this.handleVideoContainerClick.bind(this));
    }

    // Handle autoplay
    if (this.isAutoplay) {
      if (this.playButton) {
        this.playButton.style.display = 'none';
      }
      if (this.poster) {
        this.poster.style.display = 'none';
      }
      if (this.video) {
        this.video.style.display = 'block';
        // Modern browsers require muted for autoplay
        this.video.muted = true;
        this.video.play().catch((error) => {
          console.error('Autoplay failed:', error);
        });
      } else if (this.iframe) {
        this.iframe.style.display = 'block';
      }
      this.hasStartedPlaying = true;
    } else if (this.poster) {
      // Hide video/iframe initially if we have a poster
      if (this.video) this.video.style.display = 'none';
      if (this.iframe) this.iframe.style.display = 'none';
    }
  }

  handleVideoContainerClick(event) {
    // Only handle clicks directly on the container or iframe
    // Ignore clicks when using native controls
    if (
      (event.target === this.videoContainer || event.target === this.iframe) &&
      !(this.hasControls && this.hasStartedPlaying)
    ) {
      this.toggleVideo();
    }
  }

  handlePlayButton() {
    this.toggleVideo();
  }

  toggleVideo() {
    if (this.video) {
      if (this.video.paused) {
        this.playVideo();
      } else {
        this.pauseVideo();
      }
    } else if (this.iframe) {
      this.toggleIframeVideo();
    }
  }

  playVideo() {
    if (!this.hasStartedPlaying && this.poster) {
      this.poster.style.display = 'none';
      if (this.video) this.video.style.display = 'block';
      if (this.iframe) this.iframe.style.display = 'block';
      this.hasStartedPlaying = true;
    }

    if (this.video) {
      this.video.play().catch((error) => {
        console.error('Video play failed:', error);
      });
    }

    if (this.playButton) {
      this.playButton.style.display = 'none';
    }
  }

  pauseVideo() {
    this.video.pause();
    if (this.playButton) {
      this.playButton.style.display = 'block';
    }
  }

  toggleIframeVideo() {
    if (!this.playButton || this.playButton.style.display !== 'none') {
      // Video is paused, play it
      if (!this.hasStartedPlaying && this.poster) {
        this.poster.style.display = 'none';
        this.iframe.style.display = 'block';
        this.hasStartedPlaying = true;
      }

      if (this.iframe.classList.contains('js-youtube')) {
        this.iframe.contentWindow.postMessage(
          JSON.stringify({
            event: 'command',
            func: 'playVideo',
            args: '',
          }),
          '*',
        );
      } else if (this.iframe.classList.contains('js-vimeo')) {
        this.iframe.contentWindow.postMessage(
          JSON.stringify({
            method: 'play',
          }),
          '*',
        );
      }
      if (this.playButton) {
        this.playButton.style.display = 'none';
      }
    } else {
      // Video is playing, pause it
      if (this.iframe.classList.contains('js-youtube')) {
        this.iframe.contentWindow.postMessage(
          JSON.stringify({
            event: 'command',
            func: 'pauseVideo',
            args: '',
          }),
          '*',
        );
      } else if (this.iframe.classList.contains('js-vimeo')) {
        this.iframe.contentWindow.postMessage(
          JSON.stringify({
            method: 'pause',
          }),
          '*',
        );
      }
      if (this.playButton) {
        this.playButton.style.display = 'block';
      }
    }
  }

  toggleMute() {
    if (this.video) {
      this.video.muted = !this.video.muted;
      this.isMuted = this.video.muted;
    } else if (this.iframe) {
      this.isMuted = !this.isMuted;
      if (this.iframe.classList.contains('js-youtube')) {
        this.iframe.contentWindow.postMessage(
          JSON.stringify({
            event: 'command',
            func: this.isMuted ? 'mute' : 'unMute',
            args: '',
          }),
          '*',
        );
      } else if (this.iframe.classList.contains('js-vimeo')) {
        this.iframe.contentWindow.postMessage(
          JSON.stringify({
            method: 'setVolume',
            value: this.isMuted ? 0 : 1,
          }),
          '*',
        );
      }
    }
    this.updateMuteButtonState();
  }

  updateMuteButtonState() {
    if (this.muteButton) {
      this.muteButton.classList.toggle('is-muted', this.isMuted);
    }
  }
}

if (!customElements.get('video-section')) {
  customElements.define('video-section', VideoSection);
}
